import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Database,
  Edit,
  ArrowLeft,
  Trash2,
  Loader2,
  Building2,
  Calendar,
  Activity,
  Users,
  BarChart3,
  FileText,
  Plus,
  Settings,
  Eye,
  Download,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Shield,
  Info
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { ROUTES, USER_ROLES } from '@/utils/constants'
import RecordsTab from '@/components/RecordsTab'
import DraggableAttributesList from '@/components/DraggableAttributesList'
import ExportModal from '@/components/ExportModal'
import { useAuth } from '@/context/AuthContext'
import { smartCacheInvalidation } from '@/utils/cacheInvalidation'
import { exportsService } from '@/services/exports'
import toast from 'react-hot-toast'

const DataInputDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission, user } = useAuth()
  const [searchParams] = useSearchParams()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [attributesViewMode, setAttributesViewMode] = useState('table') // 'table' or 'drag'

  // Set initial tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['overview', 'attributes', 'records'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  // Attributes tab state
  const [attributesSearch, setAttributesSearch] = useState('')
  const [attributesStatusFilter, setAttributesStatusFilter] = useState('all') // 'all', 'active', 'inactive'
  const [attributesPage, setAttributesPage] = useState(1)
  const [attributesPageSize, setAttributesPageSize] = useState(10)

  const {
    data: dataInput,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['dataInput', id],
    () => dataInputsService.getById(id),
    {
      onError: (error) => {
        toast.error('Failed to load data input details')
        console.error('Error loading data input:', error)
      }
    }
  )

  // Get record count for overview
  const {
    data: recordsData
  } = useQuery(
    ['dataInputRecords', id],
    () => dataInputsService.getRecords(id, { limit: 1 }),
    {
      enabled: !!dataInput,
      onError: (error) => {
        console.error('Error loading records count:', error)
      }
    }
  )

  // Delete data input mutation
  const deleteDataInputMutation = useMutation(
    () => dataInputsService.delete(id),
    {
      onSuccess: () => {
        toast.success('Data input deleted successfully')
        navigate(ROUTES.DATA_INPUTS)
      },
      onError: (error) => {
        toast.error('Failed to delete data input')
        console.error('Error deleting data input:', error)
      }
    }
  )

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = () => {
    deleteDataInputMutation.mutate()
  }

  // Export functionality - consistent with Records tab
  const handleExportClick = () => {
    const activeAttributes = dataInput.attributes?.filter(attr => attr.is_active) || []
    if (!activeAttributes || activeAttributes.length === 0) {
      toast.error('No active attributes defined for export')
      return
    }
    setShowExportModal(true)
  }

  const handleExport = async (exportOptions) => {
    let loadingToast
    try {
      loadingToast = toast.loading('Preparing export... This may take a moment for large datasets.')

      // Use the same export service as Records tab
      const result = await exportsService.exportData(dataInput.id, {
        format: exportOptions.format,
        includeHeaders: exportOptions.includeHeaders,
        includeMetadata: exportOptions.includeMetadata,
        includeDeleted: exportOptions.includeDeleted,
        selectedColumns: exportOptions.selectedColumns,
        filename: exportOptions.filename || `${dataInput.name}_export`,
        filters: [], // No filters from Quick Actions
        searchQuery: null // No search from Quick Actions
      })

      // Dismiss loading toast
      toast.dismiss(loadingToast)

      if (result.success) {
        // Download the file
        exportsService.downloadBlob(result.blob, result.filename)
        toast.success(`Successfully exported ${result.recordCount} records!`)
      } else {
        toast.error('Export failed. Please try again.')
      }
    } catch (error) {
      // Dismiss loading toast if it exists
      if (loadingToast) {
        toast.dismiss(loadingToast)
      }
      console.error('Export failed:', error)
      toast.error(error.message || 'Export failed. Please try again.')
    }
  }

  // Filter and paginate attributes
  const getFilteredAndPaginatedAttributes = () => {
    if (!dataInput.attributes) return { attributes: [], total: 0, totalPages: 0 }

    let filtered = dataInput.attributes

    // Apply search filter
    if (attributesSearch.trim()) {
      const searchLower = attributesSearch.toLowerCase()
      filtered = filtered.filter(attr =>
        attr.name.toLowerCase().includes(searchLower) ||
        (attr.label && attr.label.toLowerCase().includes(searchLower)) ||
        (attr.description && attr.description.toLowerCase().includes(searchLower)) ||
        attr.data_type.toLowerCase().includes(searchLower)
      )
    }

    // Apply status filter
    if (attributesStatusFilter !== 'all') {
      filtered = filtered.filter(attr =>
        attributesStatusFilter === 'active' ? attr.is_active : !attr.is_active
      )
    }

    const total = filtered.length
    const totalPages = Math.ceil(total / attributesPageSize)

    // Apply pagination
    const startIndex = (attributesPage - 1) * attributesPageSize
    const endIndex = startIndex + attributesPageSize
    const paginatedAttributes = filtered.slice(startIndex, endIndex)

    return {
      attributes: paginatedAttributes,
      total,
      totalPages
    }
  }

  // Reset page when filters change
  const handleSearchChange = (value) => {
    setAttributesSearch(value)
    setAttributesPage(1)
  }

  const handleStatusFilterChange = (value) => {
    setAttributesStatusFilter(value)
    setAttributesPage(1)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading data input details...</span>
        </div>
      </div>
    )
  }

  if (error || !dataInput) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <Database className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Data input not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The data input you're looking for doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate(ROUTES.DATA_INPUTS)}
              className="btn-primary btn-sm"
            >
              Back to Data Inputs
            </button>
          </div>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Eye },
    { id: 'attributes', name: 'Attributes', icon: Settings },
    { id: 'records', name: 'Records', icon: FileText },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => navigate(ROUTES.DATA_INPUTS)}
            className="btn-outline btn-sm flex items-center self-start"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Back to Data Inputs</span>
            <span className="sm:hidden">Back</span>
          </button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{dataInput.name}</h1>
            <p className="mt-1 text-sm text-gray-600">
              Data input details and management
            </p>
          </div>
        </div>
        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
          <Link
            to={`/data-inputs/${dataInput.id}/edit`}
            className="btn-primary btn-sm flex items-center justify-center"
          >
            <Edit className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Edit Data Input</span>
            <span className="sm:hidden">Edit</span>
          </Link>
          <button
            onClick={handleDelete}
            className="btn-sm bg-red-100 text-red-700 hover:bg-red-200 flex items-center justify-center"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="overflow-x-auto">
          <nav className="-mb-px flex space-x-6 sm:space-x-8 min-w-max px-4 sm:px-0">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id)
                    navigate(`/data-inputs/${id}?tab=${tab.id}`, { replace: true })
                  }}
                  className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2 flex-shrink-0" />
                  {tab.name}
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Data Input Information */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Data Input Information</h3>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-primary-100 flex items-center justify-center">
                      <Database className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Data Input Name</div>
                      <div className="text-sm text-gray-600">{dataInput.name}</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Building2 className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Brand</div>
                      <div className="text-sm text-gray-600">
                        {dataInput.brand?.name || 'Unknown Brand'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Activity className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Status</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        dataInput.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {dataInput.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">User Isolation</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        dataInput.enable_user_isolation
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {dataInput.enable_user_isolation ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Created</div>
                      <div className="text-sm text-gray-600">
                        {new Date(dataInput.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Last Updated</div>
                      <div className="text-sm text-gray-600">
                        {new Date(dataInput.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>

                {dataInput.description && (
                  <div className="mt-6">
                    <div className="text-sm font-medium text-gray-900 mb-2">Description</div>
                    <p className="text-sm text-gray-600">{dataInput.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Statistics</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Total Records</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {recordsData?.total || 0}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Settings className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Attributes</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {dataInput.attributes?.length || 0}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Contributors</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {(() => {
                      const records = recordsData?.records || []
                      const uniqueContributors = new Set()
                      records.forEach(record => {
                        if (record.created_by) uniqueContributors.add(record.created_by)
                        if (record.updated_by && record.updated_by !== record.created_by) {
                          uniqueContributors.add(record.updated_by)
                        }
                      })
                      return uniqueContributors.size
                    })()}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Last Activity</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {(() => {
                      const records = recordsData?.records || []
                      let lastActivity = null

                      // Check data input updates
                      if (dataInput.updated_at) {
                        lastActivity = new Date(dataInput.updated_at)
                      }

                      // Check record activities
                      records.forEach(record => {
                        const recordUpdated = record.updated_at ? new Date(record.updated_at) : null
                        const recordCreated = record.created_at ? new Date(record.created_at) : null

                        if (recordUpdated && (!lastActivity || recordUpdated > lastActivity)) {
                          lastActivity = recordUpdated
                        }
                        if (recordCreated && (!lastActivity || recordCreated > lastActivity)) {
                          lastActivity = recordCreated
                        }
                      })

                      return lastActivity ? lastActivity.toLocaleDateString() : 'Never'
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              </div>
              <div className="p-6 space-y-3">
                <Link
                  to={`/data-inputs/${dataInput.id}/records/create`}
                  className="w-full btn-primary btn-sm flex items-center justify-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Record
                </Link>

                {/* Export button - only for admin and manager roles */}
                {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.MANAGER) && (
                  <button
                    onClick={handleExportClick}
                    className="w-full btn-outline btn-sm flex items-center justify-center"
                    disabled={!dataInput.attributes || dataInput.attributes.filter(attr => attr.is_active).length === 0}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Data
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attributes Tab */}
      {activeTab === 'attributes' && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
              <h3 className="text-lg font-medium text-gray-900">Data Input Attributes</h3>

              {/* View Mode Toggle - Only show in table view */}
              {attributesViewMode === 'table' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setAttributesViewMode('table')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      attributesViewMode === 'table'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    title="Table View"
                  >
                    <Database className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setAttributesViewMode('drag')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      attributesViewMode === 'drag'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    title="Drag & Drop View"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Back to Table View button - Only show in drag view */}
              {attributesViewMode === 'drag' && (
                <button
                  onClick={() => setAttributesViewMode('table')}
                  className="px-3 py-1 text-sm rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  title="Back to Table View"
                >
                  ← Back to Table View
                </button>
              )}
            </div>

            {hasPermission('create') && (
              <Link
                to={`/data-inputs/${dataInput.id}/attributes/create`}
                className="btn-primary btn-sm flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Attribute
              </Link>
            )}
          </div>

          {/* Column Order Information - Only for Table View */}
          {attributesViewMode === 'table' && (
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-blue-50">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Column Order Information</h4>
                  <p className="text-sm text-blue-800 mt-1">
                    The order of attributes determines the column order in the Records tab.
                    To reorder attributes and change column layout, switch to the
                    <button
                      onClick={() => setAttributesViewMode('drag')}
                      className="mx-1 text-blue-700 underline hover:text-blue-900 font-medium"
                    >
                      Drag & Drop view
                    </button>
                    above.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Search and Filter Controls - Only for Table View */}
          {attributesViewMode === 'table' && (
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search attributes..."
                    value={attributesSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="sm:w-48">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    value={attributesStatusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Page Size Selector */}
              <div className="sm:w-32">
                <select
                  value={attributesPageSize}
                  onChange={(e) => {
                    setAttributesPageSize(Number(e.target.value))
                    setAttributesPage(1)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>
          </div>
          )}

          <div className="p-6">
            {/* Conditional Rendering Based on View Mode */}
            {attributesViewMode === 'drag' ? (
              /* Drag and Drop View */
              <DraggableAttributesList
                attributes={dataInput.attributes?.sort((a, b) => a.display_order - b.display_order) || []}
                dataInputId={dataInput.id}
                hasEditPermission={hasPermission('edit')}
                onReorderComplete={() => {
                  // Refresh data after reordering
                  queryClient.invalidateQueries(['dataInput', id])
                }}
              />
            ) : (
              /* Table View */
              (() => {
                const { attributes, total, totalPages } = getFilteredAndPaginatedAttributes()

                if (dataInput.attributes && dataInput.attributes.length > 0) {
                  if (attributes.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <div className="text-gray-500">
                          {attributesSearch || attributesStatusFilter !== 'all'
                            ? 'No attributes match your search criteria.'
                            : 'No attributes found.'}
                        </div>
                        {(attributesSearch || attributesStatusFilter !== 'all') && (
                          <button
                            onClick={() => {
                              setAttributesSearch('')
                              setAttributesStatusFilter('all')
                              setAttributesPage(1)
                            }}
                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    )
                  }

                return (
                  <>
                    {/* Results Summary */}
                    <div className="mb-4 text-sm text-gray-600">
                      Showing {((attributesPage - 1) * attributesPageSize) + 1} to {Math.min(attributesPage * attributesPageSize, total)} of {total} attributes
                    </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                        Name
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                        Label
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                        Type
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                        Value Options
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                        Format
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                        Required
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                        Status
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attributes.map((attribute) => (
                      <tr key={attribute.id}>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="truncate max-w-[120px]" title={attribute.name}>
                            {attribute.name}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="truncate max-w-[120px]" title={attribute.label || '-'}>
                            {attribute.label || '-'}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {attribute.data_type}
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500">
                          {attribute.allowed_values && attribute.allowed_values.length > 0 ? (
                            <div className="max-w-xs">
                              <div className="flex flex-wrap gap-1">
                                {attribute.allowed_values.slice(0, 3).map((value, index) => (
                                  <span key={index} className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                    {value}
                                  </span>
                                ))}
                                {attribute.allowed_values.length > 3 && (
                                  <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                    +{attribute.allowed_values.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {attribute.format ? (
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {attribute.format}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {attribute.is_required ? (
                            <span className="text-red-600">Required</span>
                          ) : (
                            <span className="text-gray-400">Optional</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            attribute.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {attribute.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {hasPermission('edit') && (
                              <Link
                                to={`/data-inputs/${dataInput.id}/attributes/${attribute.id}/edit`}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit Attribute"
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <span>
                      Page {attributesPage} of {totalPages}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setAttributesPage(Math.max(1, attributesPage - 1))}
                      disabled={attributesPage === 1}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const pages = []
                        const maxVisiblePages = 5
                        let startPage = Math.max(1, attributesPage - Math.floor(maxVisiblePages / 2))
                        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

                        if (endPage - startPage + 1 < maxVisiblePages) {
                          startPage = Math.max(1, endPage - maxVisiblePages + 1)
                        }

                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => setAttributesPage(i)}
                              className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
                                i === attributesPage
                                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {i}
                            </button>
                          )
                        }
                        return pages
                      })()}
                    </div>

                    <button
                      onClick={() => setAttributesPage(Math.min(totalPages, attributesPage + 1))}
                      disabled={attributesPage === totalPages}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              )}
                  </>
                )
              } else {
                return (
                  <div className="text-center py-12">
                    <Settings className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No attributes</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by creating your first attribute.
                    </p>
                    <div className="mt-6">
                      {hasPermission('create') && (
                        <Link
                          to={`/data-inputs/${dataInput.id}/attributes/create`}
                          className="btn-primary btn-sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Attribute
                        </Link>
                      )}
                    </div>
                  </div>
                )
              }
              })()
            )}
          </div>
        </div>
      )}

      {/* Records Tab */}
      {activeTab === 'records' && (
        <RecordsTab dataInput={dataInput} />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Data Input
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete <strong>{dataInput.name}</strong>?
                        This action cannot be undone and will remove all associated records and attributes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={confirmDelete}
                  disabled={deleteDataInputMutation.isLoading}
                >
                  {deleteDataInputMutation.isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Data Input'
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteDataInputMutation.isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dataInput={dataInput}
        onExport={handleExport}
      />
    </div>
  )
}

export default DataInputDetail